import { assert, Test } from "oc-e2e-testrunner";
import { MultiMap } from "oc-commons/MultiMap";
import { MultiBag } from "oc-commons/MultiBag";
import { Bag } from "oc-commons/Bag2";

export default {
  name: "TestMultiMap",
};

export const testAddAndGet: Test = async () => {
  const map = new MultiMap<[string, number], string>();

  map.set(["key1", 1], "value1");

  const value = map.get(["key1", 1]);
  assert.same(value, "value1", "Wert konnte nicht korrekt abgerufen werden");
};

export const testOverwrite: Test = async () => {
  const map = new MultiMap<[string, number], string>();

  map.set(["key1", 1], "value1");
  map.set(["key1", 1], "value2");

  const value = map.get(["key1", 1]);
  assert.same(value, "value2", "Wert wurde nicht korrekt überschrieben");
};

export const testGetNonExistent: Test = async () => {
  const map = new MultiMap<[string, number], string>();

  const value = map.get(["key1", 1]);
  assert.isNull(value, "Wert sollte null sein");
};

export const testDifferentKeyArrayLengths: Test = async () => {
  const map = new MultiMap<any, string>();

  map.set(["key1", 1], "value1");
  try {
    map.set(["key2"], "value2");
  } catch (e) {
    assert.equals(e["message"], "keys must be an array of length 2");
  }
};

export const testEmptyKeys: Test = async () => {
  const map = new MultiMap<[], string>();
  try {
    map.set([], "value1");
  } catch (e) {
    assert.equals(e["message"], "keys can not be an empty array");
  }
};

export const testNullUndefinedKeys: Test = async () => {
  const map = new MultiMap<[string | null, number | undefined], string>();

  map.set([null, undefined], "value1");

  const value = map.get([null, undefined]);
  assert.same(value, "value1", "Wert konnte nicht korrekt abgerufen werden");
};

// Test mit gleichen Schlüsselwerten aber unterschiedlichen Typen
export const testSameKeyValuesDifferentTypes: Test = async () => {
  const map = new MultiMap<[string | number], string>();

  map.set([1], "value1");
  map.set(["1"], "value2");

  const value1 = map.get([1]);
  const value2 = map.get(["1"]);

  assert.same(value1, "value1", "Wert 1 konnte nicht korrekt abgerufen werden");
  assert.same(value2, "value2", "Wert 2 konnte nicht korrekt abgerufen werden");
};

// Test für die toArray Methode
export const testToArray: Test = async () => {
  const map = new MultiMap<any, string>();

  map.set(["key1", 1], "value1");
  map.set(["key1", 2], "value2");
  map.set(["key2", 2], "value3");
  map.set(["key2", undefined], "value4");

  const array = map.toArray();
  assert.deepEquals(
    array,
    [
      ["key1", 1, "value1"],
      ["key1", 2, "value2"],
      ["key2", 2, "value3"],
      ["key2", undefined, "value4"],
    ],
    "toArray liefert nicht das erwartete Ergebnis"
  );
};

export const testIncrement: Test = async () => {
  const bag = new MultiBag<[string, number]>();

  bag.inc(["key1", 1], 1);
  bag.inc(["key2", 2], 5);

  assert.same(bag.get(["key1", 1]), 1);
  assert.same(bag.get(["key2", 2]), 5);
};

export const testMultipleIncrement: Test = async () => {
  const bag = new MultiBag<[string, number]>();

  bag.inc(["key1", 1], 0);
  bag.inc(["key1", 1], 2);
  bag.inc(["key1", 1], 3);

  assert.same(bag.get(["key1", 1]), 5);
};

export const testMultiBagToArray: Test = async () => {
  const bag = new MultiBag<[string, number]>();

  bag.inc(["key1", 1], 2);
  bag.inc(["key2", 2], 5);

  const array = bag.toArray();

  assert.deepEquals(
    [
      ["key1", 1, 2],
      ["key2", 2, 5],
    ],
    array,
    "toArray liefert nicht das erwartete Ergebnis"
  );
};

export const testIncrementEdgeCases: Test = async () => {
  const bag = new MultiBag<any>();

  bag.inc([null, NaN], undefined);
  bag.inc(["key", NaN], 5);
  bag.inc([undefined, 3], NaN);
  bag.inc([null, 4], NaN);
  bag.inc([undefined, 7], 1);
  bag.inc([undefined, 6], undefined);
  bag.inc([undefined, 5], null);

  assert.isNaN(bag.get([null, NaN]));
  assert.same(5, bag.get(["key", NaN]));
  assert.isNaN(bag.get([undefined, 3]));
  assert.isNaN(bag.get([null, 4]));
  assert.same(bag.get([undefined, 7]), 1);
  assert.isNaN(bag.get([undefined, 6]));
  assert.same(0, bag.get([undefined, 5]));
};

// Test für die getSumOfSubTree Methode mit gleicher Schlüssellänge
export const testgetSumOfSubTree: Test = async () => {
  const bag = new MultiBag<string[]>();

  bag.inc(["a", "x", "1"], 5);
  bag.inc(["a", "y", "1"], 10);
  bag.inc(["b", "x", "1"], 15);
  bag.inc(["b", "y", "2"], 20);

  const sum1 = bag.getSumOfSubTree([]); // Sollte die Summe aller Werte in der MultiBag zurückgeben
  const sum2 = bag.getSumOfSubTree(["a"]); // Sollte die Summe der Werte unterhalb von ['a'] zurückgeben
  const sum3 = bag.getSumOfSubTree(["b"]); // Sollte die Summe der Werte unterhalb von ['b'] zurückgeben
  const sum4 = bag.getSumOfSubTree(["a", "x"]); // Sollte die Summe der Werte unterhalb von ['a', 'x'] zurückgeben
  const sum5 = bag.getSumOfSubTree(["b", "y"]); // Sollte die Summe der Werte unterhalb von ['b', 'y'] zurückgeben

  assert.same(sum1, 50, "Summe aller Werte sollte 50 sein");
  assert.same(sum2, 15, "Summe der Werte unterhalb von ['a'] sollte 15 sein");
  assert.same(sum3, 35, "Summe der Werte unterhalb von ['b'] sollte 35 sein");
  assert.same(sum4, 5, "Summe der Werte unterhalb von ['a', 'x'] sollte 5 sein");
  assert.same(sum5, 20, "Summe der Werte unterhalb von ['b', 'y'] sollte 20 sein");
};

// Test für die inc und dec Methoden
export const testIncDec: Test = async () => {
  const bag = new Bag<string>();

  bag.inc("key1", 1);
  bag.inc("key2", 5);
  bag.dec("key2", 2);

  assert.same(bag.get("key1"), 1, "Wert für 'key1' sollte 1 sein");
  assert.same(bag.get("key2"), 3, "Wert für 'key2' sollte 3 sein");
};

// Test für die multiply und add Methoden
export const testMultiplyAdd: Test = async () => {
  const bag = new Bag<string>();

  bag.inc("key1", 2);
  bag.inc("key2", 3);

  bag.multiply(2);
  bag.add(1);

  assert.same(bag.get("key1"), 5, "Wert für 'key1' sollte 5 sein");
  assert.same(bag.get("key2"), 7, "Wert für 'key2' sollte 7 sein");
};

// Test für die from Methode
export const testFrom: Test = async () => {
  const bag = new Bag<string>();
  const map = new Map<string, number>([
    ["key1", 3],
    ["key2", 4],
  ]);

  bag.from(map);

  assert.same(bag.get("key1"), 3, "Wert für 'key1' sollte 3 sein");
  assert.same(bag.get("key2"), 4, "Wert für 'key2' sollte 4 sein");
};

// Test für die incAll und decAll Methoden
export const testIncAllDecAll: Test = async () => {
  const bag1 = new Bag<string>();
  const bag2 = new Bag<string>();

  bag1.inc("key1", 3);
  bag1.inc("key2", 4);

  bag2.incAll(bag1);
  bag2.decAll(bag1);

  assert.same(bag2.get("key1"), 0, "Wert für 'key1' sollte 0 sein");
  assert.same(bag2.get("key2"), 0, "Wert für 'key2' sollte 0 sein");
};

// Test für die toArray Methode
export const testToArrayBag: Test = async () => {
  const bag = new Bag<string>();

  bag.inc("key1", 3);
  bag.inc("key2", 4);

  const array = bag.toArray();

  assert.deepEquals(
    array,
    [
      ["key1", 3],
      ["key2", 4],
    ],
    "toArray liefert nicht das erwartete Ergebnis"
  );
};

// Test für Grenzfälle in der inc Methode
export const testBagIncrementEdgeCases: Test = async () => {
  const bag = new Bag<string>();

  // Test für negative Inkrementierung
  bag.inc("key1", -1);
  assert.same(bag.get("key1"), -1, "Wert für 'key1' sollte -1 sein nach negativer Inkrementierung");

  // Test für Inkrementierung um 0
  bag.inc("key1", 0);
  assert.same(
    bag.get("key1"),
    -1,
    "Wert für 'key1' sollte unverändert bleiben nach Inkrementierung um 0"
  );

  // Test für Inkrementierung mit sehr großem Betrag
  bag.inc("key1", Number.MAX_SAFE_INTEGER);
  assert.same(
    bag.get("key1"),
    Number.MAX_SAFE_INTEGER - 1,
    "Wert für 'key1' sollte inkrementiert sein auf Maximum safe integer - 1"
  );

  // Test für Inkrementierung mit NaN
  bag.inc("key1", NaN);
  assert.isNaN(bag.get("key1"), "Wert für 'key1' sollte NaN sein nach Inkrementierung mit NaN");

  // Test für Inkrementierung mit undefined (sollte als 0 behandelt werden)
  // @ts-ignore: Argument of type 'undefined' is not assignable to parameter of type 'number'.
  bag.inc("key1", undefined);
  assert.isNaN(
    bag.get("key1"),
    "Wert für 'key1' sollte unverändert bleiben nach Inkrementierung mit undefined"
  );

  // Test für Inkrementierung mit Infinity
  bag.inc("key2", 1);
  bag.inc("key2", Infinity);
  assert.same(
    bag.get("key2"),
    Infinity,
    "Wert für 'key2' sollte Infinity sein nach Inkrementierung mit Infinity"
  );
};

export const testMultiBagIncrementEdgeCases: Test = async () => {
  const bag = new MultiBag<[string]>();

  // Test für negative Inkrementierung
  bag.inc(["key1"], -1);
  assert.same(
    bag.get(["key1"]),
    -1,
    "Wert für 'key1' sollte -1 sein nach negativer Inkrementierung"
  );

  // Test für Inkrementierung um 0
  bag.inc(["key1"], 0);
  assert.same(
    bag.get(["key1"]),
    -1,
    "Wert für 'key1' sollte unverändert bleiben nach Inkrementierung um 0"
  );

  // Test für Inkrementierung mit sehr großem Betrag
  bag.inc(["key1"], Number.MAX_SAFE_INTEGER);
  assert.same(
    bag.get(["key1"]),
    Number.MAX_SAFE_INTEGER - 1,
    "Wert für 'key1' sollte inkrementiert sein auf Maximum safe integer - 1"
  );

  // Test für Inkrementierung mit NaN
  bag.inc(["key1"], NaN);
  assert.isNaN(bag.get(["key1"]), "Wert für 'key1' sollte NaN sein nach Inkrementierung mit NaN");

  // Test für Inkrementierung mit undefined (sollte als 0 behandelt werden)
  // @ts-ignore: Argument of type 'undefined' is not assignable to parameter of type 'number'.
  bag.inc(["key1"], undefined);
  assert.isNaN(
    bag.get(["key1"]),
    "Wert für 'key1' sollte unverändert bleiben nach Inkrementierung mit undefined"
  );

  // Test für Inkrementierung mit Infinity
  bag.inc(["key2"], 1);
  bag.inc(["key2"], Infinity);
  assert.same(
    bag.get(["key2"]),
    Infinity,
    "Wert für 'key2' sollte Infinity sein nach Inkrementierung mit Infinity"
  );
};

export const testBagIncrementEdgeCases2: Test = async () => {
  const bag = new Bag<any>();

  bag.inc(null, 1);
  bag.inc(undefined, 1);
  bag.inc(NaN, 1);
  bag.inc("a", 1);
  bag.inc("b", NaN);
  bag.inc("c", undefined);
  bag.inc("d", null);

  assert.same(1, bag.get(null));
  assert.same(1, bag.get(undefined));
  assert.same(1, bag.get(NaN));
  assert.same(1, bag.get("a"));
  assert.isNaN(bag.get("b"));
  assert.isNaN(bag.get("c"));
  assert.same(0, bag.get("d"));
  assert.same(0, bag.get("e"));
};
